import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Box, IconButton, Divider, Grid, Typography, Link } from '@mui/material';
import CustomDrawer from 'components/CustomDrawer';
import { IS_SANDBOX_ENV } from 'helper/constant';
import { truncateUserName } from 'helper/utility';
import SetupGuideModal from 'layouts/V2/Dashboard/components/SetupGuideModal';
import PropTypes from 'prop-types';
import routeEndpoints from 'route/endpoints';

import { selectIsSetupGuideActive } from 'redux/selectors/general';

import styles from '../sidebar.module.css';

import { ReactComponent as LeftArrow } from 'asset/IconsPack/at_chevron-left-filled.svg';
import { ReactComponent as FileIcon } from 'asset/IconsPack/at_file-06.svg';
import { ReactComponent as LogoutIcon } from 'asset/IconsPack/at_log-out-03.svg';
import { ReactComponent as SettingsIcon } from 'asset/IconsPack/at_settings-01.svg';
import { ReactComponent as TicketIcon } from 'asset/IconsPack/at_ticket-01.svg';
import { ReactComponent as Profile } from 'asset/IconsPack/at_user-circle.svg';
import { ReactComponent as AuthenticateLogo } from 'asset/img/authenticate_rgb.svg';

import NavigationList from './NavigationList';

function SmallScreenSidebar({ openSidebar, handleToggler, firstName, isMobile }) {
    const history = useHistory();
    const isSetupGuideActive = useSelector(selectIsSetupGuideActive);
    const [openSetupGuideModal, setOpenSetupGuideModal] = useState(false);

    const handleLogout = () => {
        history.push(routeEndpoints.logout);
    };

    const handleSetupGuide = () => {
        setOpenSetupGuideModal(true);
    };

    const handleCloseSetupGuide = () => {
        setOpenSetupGuideModal(false);
        handleToggler();
    };

    return (
        <>
            <CustomDrawer
                anchor="left"
                variant="temporary"
                className={styles.tabletSidebar}
                open={openSidebar}
                onClose={handleToggler}
            >
                <Box className={styles.tabletNavHeader}>
                    <IconButton onClick={handleToggler} className={styles.toggleButton}>
                        <LeftArrow className={styles.leftArrow} />
                    </IconButton>
                    <Divider orientation="vertical" flexItem className={styles.divider} />
                    <AuthenticateLogo className={styles.authenticateLogo} />
                </Box>
                <NavigationList />
                {isMobile && (
                    <Box className={styles.userDetailsBox}>
                        <Grid className={styles.userNameBox}>
                            <Profile className={styles.profileLogo} />
                            <Typography className={styles.userNameLabel}>
                                Signed in as <span>{truncateUserName(firstName, 20)}</span>
                            </Typography>
                        </Grid>
                        <Divider className={styles.divider} />
                        <Box className={styles.userOptionsBox}>
                            <Link
                                href={
                                    IS_SANDBOX_ENV
                                        ? 'https://info.authenticate.com/sandbox'
                                        : 'https://docs.authenticate.com/'
                                }
                                target="_blank"
                                rel="noreferrer"
                                className={styles.userMenuItem}
                            >
                                <FileIcon className={styles.fileIcon} /> Technical Docs
                            </Link>
                            {!IS_SANDBOX_ENV && !isSetupGuideActive && (
                                <Link
                                    component="button"
                                    variant="body2"
                                    className={styles.userMenuItem}
                                    onClick={handleSetupGuide}
                                >
                                    <SettingsIcon className={styles.userMenuIcon} /> Setup Guide
                                </Link>
                            )}
                            <Link
                                href="https://info.authenticate.com/kb-tickets/new"
                                target="_blank"
                                rel="noreferrer"
                                className={styles.userMenuItem}
                            >
                                <TicketIcon className={styles.userMenuIcon} /> Raise a Support
                                Ticket
                            </Link>
                            <Link
                                component="button"
                                variant="body2"
                                className={styles.userMenuItem}
                                onClick={handleLogout}
                            >
                                <LogoutIcon className={styles.userMenuIcon} /> Logout
                            </Link>
                        </Box>
                    </Box>
                )}
            </CustomDrawer>
            <SetupGuideModal open={openSetupGuideModal} onClose={handleCloseSetupGuide} />
        </>
    );
}

SmallScreenSidebar.propTypes = {
    isMobile: PropTypes.bool,
    openSidebar: PropTypes.bool,
    handleToggler: PropTypes.func,
    firstName: PropTypes.string,
};

export default SmallScreenSidebar;
